import Serializer from '@ember-data/serializer';
import { decorateClass, isDecoratorArgs } from 'decorator-setup';

/**
 * Add relationship item to resourceHash
 * @param resourceHash Second argument given to extractRelationships
 * @param type Name of relationship to model
 * @param {{ id: string, type: string }} data Relationship data
 * @returns {*}
 */
export function addRelationship(resourceHash, type, data) {
  const relationships = resourceHash.relationships ||= {};
  const rels = relationships[type] ||= {};
  return rels.data = data;
}

/**
 * @typedef KeyForAttributeMapConfig
 * @property {string} [attribute] Mapped key for attribute
 * @property {string} [relationship] Mapped key for relationship from attribute
 * @property {false} [delete] False to not delete attribute mapped to relationship
 */
/**
 * @typedef KeyForAttributeMapping
 * @type {string|KeyForAttributeMapConfig}
 */
/**
 * Class decorator to map serialized model attributes
 * @param {{[string]:KeyForAttributeMapping}} map Mapping of local to remote model attribute keys
 * @decorator
 */
export function keyForAttribute(map) {
  if (isDecoratorArgs(...arguments)) {
    throw new Error('@keyForAttribute expects mapping object');
  }
  return function(Target) {
    if (!Serializer.isPrototypeOf(Target)) {
      throw new Error(
        '@keyForAttribute decorator should be applied to an Ember Serializer derived class',
      );
    }
    return decorateClass(class KeyForAttributeDecoratedSerializer extends Target {
      keyForAttribute(key, method) {
        return ((typeof map[key] === 'string' ? map[key] : map[key]?.attribute)
          || super.keyForAttribute(key)
        );
      }
      extractAttributes(modelClass, resourceHash) {
        // console.log('extractAttributes', modelClass, resourceHash);
        return super.extractAttributes(...arguments);
      }
      extractRelationships(modelClass, resourceHash) {
        const { attributes } = resourceHash;
        if (!attributes) {
          return super.extractRelationships(...arguments);
        }
        const idToRelationship = id => ({ id, type: modelClass.modelName });
        for (let [ key, value ] of Object.entries(attributes)) {
          const mapping = map[key];
          if (!value || !mapping || typeof mapping === 'string') {
            continue;
          }
          const isArray = Array.isArray(value);
          if (mapping.relationship && value && (!isArray || value.length)) {
            if (mapping.delete !== false) {
              delete attributes[key];
            }
            if (typeof mapping.map === 'function') {
              value = isArray ? value.map(mapping.map)
                : mapping.map(value);
            }
            addRelationship(
              resourceHash,
              mapping.relationship,
              isArray ? value.map(idToRelationship) : idToRelationship(value)
            );
          }
        }
        return super.extractRelationships(...arguments);
      }
    });
  };
}

/**
 * @typedef KeyForRelationshipMapConfig
 * @property {string} [attribute] Mapped key for attribute
 * @property {string} [relationship] Mapped key for relationship from attribute
 * @property {false} [delete] False to not delete relationship mapped to attribute
 */
/**
 * @typedef KeyForRelationshipMapping
 * @type {string|KeyForRelationshipMapConfig}
 */
/**
 * Class decorator to map serialized model relationships
 * @param {{[string]:KeyForRelationshipMapping}} map Mapping of local to remote model relationship keys
 * @decorator
 */
export function keyForRelationship(map) {
  if (isDecoratorArgs(...arguments)) {
    throw new Error('@keyForRelationship expects mapping object');
  }
  return function (Target) {
    if (!Serializer.isPrototypeOf(Target)) {
      throw new Error(
        '@keyForRelationship decorator should be applied to an Ember Serializer derived class',
      );
    }
    return decorateClass(class KeyForRelationshipDecoratedSerializer extends Target {
      keyForRelationship(key) {
        return super.keyForRelationship(map[key] || key);
      }
      extractAttributes(modelClass, resourceHash) {
        const { relationships } = resourceHash;
        if (!relationships) {
          return super.extractRelationships(...arguments);
        }
        const attributes = resourceHash.attributes ||= {};
        for (let [ key, value ] of Object.entries(relationships)) {
          const mapping = map[key];
          value = this.extractRelationship(value).data;
          if (!value || !mapping || typeof mapping === 'string') {
            continue;
          }
          const isArray = Array.isArray(value);
          if (mapping.attribute && value && (!isArray || value.length)) {
            if (mapping.delete !== false) {
              delete relationships[key];
            }
            if (typeof mapping.map === 'function') {
              value = Array.isArray(value) ? value.map(mapping.map)
                : mapping.map(value);
            }
            attributes[mapping.attribute] = value;
          }
        }
        return super.extractAttributes(...arguments);
      }
    });
  };
}
